/** Keep in sync with backend `lib/cardioTracking.ts`. */
export type TrackingLevel = 'quick' | 'training' | 'full';
export type ZoneModel = 'max_hr' | 'lthr' | 'hrr';

export const TRACKING_LEVELS: TrackingLevel[] = ['quick', 'training', 'full'];

/** Bump this to treat every account as unasked and show the tracking-depth sheet again. */
export const TRACKING_ASK_GENERATION = 1;

export function normalizeTrackingLevel(raw: unknown, activityType: string): TrackingLevel {
  const s = String(raw ?? 'quick').trim();
  const level: TrackingLevel = s === 'training' || s === 'full' ? s : 'quick';
  const max = maxLevelForActivity(activityType);
  if (level === 'full' && max !== 'full') return 'training';
  if (level === 'training' && max === 'quick') return 'quick';
  return level;
}

export function isSimpleTimeActivity(activityType: string): boolean {
  return activityType === 'outdoor_walk' || activityType === 'yoga' || activityType === 'pilates';
}

export function maxLevelForActivity(activityType: string): TrackingLevel {
  if (isSimpleTimeActivity(activityType)) return 'training';
  return 'full';
}

/** Walks stay two taps. Runs, rides, and bricks start on time + distance. */
export function defaultLevelForActivity(activityType: string): TrackingLevel {
  if (activityType === 'weightlifting' || isSimpleTimeActivity(activityType)) return 'quick';
  return 'training';
}

export type TrackingField =
  | 'duration'
  | 'notes'
  | 'perceivedEffort'
  | 'distance'
  | 'hr'
  | 'elevation'
  | 'zones'
  | 'cadence'
  | 'intervals'
  | 'power'
  | 'swim'
  | 'equipment'
  | 'segments';

const BIKE_TYPES = new Set(['stationary_bike', 'spin_class', 'outdoor_ride']);

export function isBikeActivity(activityType: string): boolean {
  return BIKE_TYPES.has(activityType);
}

export function fieldsForLevel(activityType: string, level: TrackingLevel): Set<TrackingField> {
  const fields = new Set<TrackingField>(['duration', 'notes', 'perceivedEffort']);
  if (activityType === 'weightlifting') return fields;
  if (level === 'quick') {
    // Old duration-only logs silently seeded Quick. Runs still need distance.
    if (!isSimpleTimeActivity(activityType)) {
      fields.add('distance');
      fields.add('hr');
    }
    return fields;
  }
  fields.add('distance');
  fields.add('hr');
  fields.add('elevation');
  if (level === 'full') {
    fields.add('zones');
    fields.add('cadence');
    fields.add('intervals');
    fields.add('equipment');
    if (isBikeActivity(activityType)) fields.add('power');
    if (activityType === 'swim') fields.add('swim');
  }
  if (activityType === 'multisport') fields.add('segments');
  return fields;
}

export function trackingCopy(activityType: string): {
  title: string;
  options: { level: TrackingLevel; label: string; hint: string }[];
} {
  if (activityType === 'weightlifting') {
    return {
      title: 'How do you want to log lifting?',
      options: [
        { level: 'quick', label: 'Just sets', hint: 'Weight and reps. That’s it.' },
        { level: 'training', label: 'Sets and effort', hint: 'Adds how hard each set felt (RPE).' },
        { level: 'full', label: 'Full effort', hint: 'RPE and reps in reserve.' },
      ],
    };
  }
  if (activityType === 'outdoor_walk') {
    return {
      title: 'How do you log walks?',
      options: [
        { level: 'quick', label: 'Just time', hint: 'Activity and minutes. Two taps.' },
        { level: 'training', label: 'Time and distance', hint: 'Distance, pace, and heart rate if you have them.' },
      ],
    };
  }
  if (activityType === 'swim') {
    return {
      title: 'How do you log swims?',
      options: [
        { level: 'quick', label: 'Just time', hint: 'Minutes, plus distance and heart rate if you have them.' },
        { level: 'training', label: 'Time and distance', hint: 'Distance, pace, heart rate.' },
        { level: 'full', label: 'Pool detail', hint: 'Intervals, zones, stroke, SWOLF.' },
      ],
    };
  }
  if (isBikeActivity(activityType)) {
    return {
      title: 'How do you log rides?',
      options: [
        { level: 'quick', label: 'Just time', hint: 'Minutes, plus distance and heart rate if you have them.' },
        { level: 'training', label: 'Time and distance', hint: 'Distance, speed, heart rate.' },
        { level: 'full', label: 'Full ride', hint: 'Zones, intervals, cadence, power.' },
      ],
    };
  }
  if (activityType === 'multisport') {
    return {
      title: 'How do you log bricks and tris?',
      options: [
        { level: 'quick', label: 'Just time', hint: 'One duration, plus distance if you have it.' },
        { level: 'training', label: 'Each sport', hint: 'Swim, bike, run with distance.' },
        { level: 'full', label: 'Full brick', hint: 'Segments, transitions, zones, splits.' },
      ],
    };
  }
  return {
    title: 'How much detail for this activity?',
    options: [
      { level: 'quick', label: 'Just time', hint: 'Minutes, plus distance and heart rate if you have them.' },
      { level: 'training', label: 'Time and distance', hint: 'Distance, pace, elevation, and heart rate.' },
      { level: 'full', label: 'Intervals and heart rate', hint: 'Splits, zone minutes, cadence.' },
    ],
  };
}

export type Physiology = {
  maxHr: number | null;
  restingHr: number | null;
  lthr: number | null;
  ftpWatts?: number | null;
  thresholdPaceSecPerKm?: number | null;
  zoneModel: ZoneModel;
};

export type ZoneBound = { lo: number; hi: number; label: string };

export function zoneBoundsFor(model: ZoneModel, phys: Physiology | null): ZoneBound[] {
  const labels = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];
  const pair = (lo: number, hi: number, i: number): ZoneBound => ({
    lo: Math.max(1, Math.round(lo)),
    hi: Math.max(1, Math.round(hi)),
    label: labels[i],
  });
  if (model === 'lthr' && phys?.lthr && phys.lthr > 80) {
    const t = phys.lthr;
    return [
      pair(t * 0.7, t * 0.81, 0),
      pair(t * 0.81, t * 0.9, 1),
      pair(t * 0.9, t * 0.96, 2),
      pair(t * 0.96, t * 1.02, 3),
      pair(t * 1.02, t * 1.15, 4),
    ];
  }
  if (model === 'hrr' && phys?.maxHr && phys?.restingHr && phys.maxHr > phys.restingHr) {
    const r = phys.restingHr;
    const reserve = phys.maxHr - r;
    return [0.5, 0.6, 0.7, 0.8, 0.9].map((pct, i) => {
      const next = i === 4 ? 1 : [0.6, 0.7, 0.8, 0.9, 1][i];
      return pair(r + reserve * pct, r + reserve * next, i);
    });
  }
  const max = phys?.maxHr && phys.maxHr > 90 ? phys.maxHr : 190;
  return [0.5, 0.6, 0.7, 0.8, 0.9].map((pct, i) => {
    const next = i === 4 ? 1 : [0.6, 0.7, 0.8, 0.9, 1][i];
    return pair(max * pct, max * next, i);
  });
}

export type IntervalPrescription = {
  reps: number;
  workDistanceM?: number | null;
  workSeconds?: number | null;
  targetPaceSecPerKm?: number | null;
  restSeconds?: number | null;
};

export type ZoneMinutes = { z1?: number; z2?: number; z3?: number; z4?: number; z5?: number };

export function paceSecPerKm(distanceMeters: number | null | undefined, durationSeconds: number | null | undefined): number | null {
  if (!distanceMeters || distanceMeters <= 0 || !durationSeconds || durationSeconds <= 0) return null;
  return durationSeconds / (distanceMeters / 1000);
}

export function formatPace(secPerKm: number | null | undefined, imperial: boolean): string {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '—';
  const sec = imperial ? secPerKm * 1.60934 : secPerKm;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}${imperial ? '/mi' : '/km'}`;
}

export function parsePaceToSecPerKm(text: string, imperial: boolean): number | null {
  const t = text.trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) {
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) return null;
    return imperial ? n * 60 * 1.60934 : n * 60;
  }
  const sec = Number(m[1]) * 60 + Number(m[2]);
  return imperial ? sec * 1.60934 : sec;
}

export function metersFromDistanceInput(raw: string, imperial: boolean): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n === 0) return null;
  return imperial ? n * 1609.34 : n * 1000;
}

export function distanceInputFromMeters(meters: number | null | undefined, imperial: boolean): string {
  if (meters == null || !Number.isFinite(meters) || meters <= 0) return '';
  const v = imperial ? meters / 1609.34 : meters / 1000;
  return String(Math.round(v * 100) / 100);
}

export function parseMmSsToSeconds(text: string): number | null {
  const t = text.trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,3}):(\d{2})$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

export function secondsToMmSs(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function cardioWarnings(input: {
  durationMinutes: number | null;
  distanceMeters: number | null;
  avgHr: number | null;
  maxHr: number | null;
  activityType: string;
}): string[] {
  const out: string[] = [];
  const dur = input.durationMinutes;
  if (dur != null && (dur < 1 || dur > 600)) {
    out.push('That’s an unusual duration — double-check it if you didn’t mean a very short or very long session.');
  }
  const pace = paceSecPerKm(input.distanceMeters, dur != null ? dur * 60 : null);
  if (pace != null && pace < 150 && input.activityType.includes('walk')) {
    out.push('That pace is very fast for a walk. Save anyway if it’s right.');
  }
  if (pace != null && pace < 120) {
    out.push('That pace is unusually fast. Save anyway if you copied it off the watch.');
  }
  if (input.avgHr != null && (input.avgHr < 40 || input.avgHr > 220)) {
    out.push('Average heart rate looks off. We’ll still save it.');
  }
  if (input.maxHr != null && input.avgHr != null && input.maxHr < input.avgHr) {
    out.push('Max HR is lower than average HR.');
  }
  return out;
}
